import { Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ActiveUnitProvider } from "@/contexts/ActiveUnitContext";
import { ActivePeriodoLetivoProvider } from "@/contexts/ActivePeriodoLetivoContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { DashboardGuard } from "@/components/auth/DashboardGuard";
import { HomeRedirect } from "@/components/auth/HomeRedirect";
import { GlobalDashboardGuard } from "@/components/auth/GlobalDashboardGuard";



import { AppLayout } from "@/components/layout/AppLayout";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

// ── Lazy-loaded pages ──────────────────────────────────────────────────────
const Login = lazyWithRetry(() => import("@/pages/Login"), 'Login');
const OAuthConsent = lazyWithRetry(() => import("@/pages/OAuthConsent"), 'OAuthConsent');
const AuthCallback = lazyWithRetry(() => import("@/pages/AuthCallback"), 'AuthCallback');
const AuthConfirm = lazyWithRetry(() => import("@/pages/AuthConfirm"), 'AuthConfirm');
const ResetPassword = lazyWithRetry(() => import("@/pages/ResetPassword"), 'ResetPassword');
const AceitarConvite = lazyWithRetry(() => import("@/pages/AceitarConvite"), 'AceitarConvite');
const Confianca = lazyWithRetry(() => import("@/pages/Confianca"), 'Confianca');
const Dashboard = lazyWithRetry(() => import("@/pages/Dashboard"), 'Dashboard');
const DevPerfPanel = lazyWithRetry(
  () => import("@/components/dev/DevPerfPanel").then((mod) => ({ default: mod.DevPerfPanel })),
  'DevPerfPanel',
);

const Members = lazyWithRetry(() => import("@/pages/iam/Members"), 'Members');
const Audit = lazyWithRetry(() => import("@/pages/iam/Audit"), 'Audit');
const AuditoriaDadosBancarios = lazyWithRetry(() => import("@/pages/iam/AuditoriaDadosBancarios"), 'AuditoriaDadosBancarios');
const AdminEmails = lazyWithRetry(() => import("@/pages/AdminEmails"), 'AdminEmails');
const Professores = lazyWithRetry(() => import("@/pages/Professores"), 'Professores');
const Pessoas = lazyWithRetry(() => import("@/pages/Pessoas"), 'Pessoas');
const ImpressaoProvas = lazyWithRetry(() => import("@/pages/ImpressaoProvas"), 'ImpressaoProvas');
const CheckinImpressao = lazyWithRetry(() => import("@/pages/CheckinImpressao"), 'CheckinImpressao');
const EquipamentosGestao = lazyWithRetry(() => import("@/pages/gestao/equipamentos/EquipamentosGestao"), 'EquipamentosGestao');
const AtivosScan = lazyWithRetry(() => import("@/pages/AtivosScan"), 'AtivosScan');
const Laboratorios = lazyWithRetry(() => import("@/pages/Laboratorios"), 'Laboratorios');
const Unidades = lazyWithRetry(() => import("@/pages/Unidades"), 'Unidades');
const NotFound = lazyWithRetry(() => import("@/pages/NotFound"), 'NotFound');
const ProvasScan = lazyWithRetry(() => import("@/pages/ProvasScan"), 'ProvasScan');
const CalendarioAcademico = lazyWithRetry(() => import("@/pages/CalendarioAcademico"), 'CalendarioAcademico');
const Cursos = lazyWithRetry(() => import("@/pages/Cursos"), 'Cursos');
const Disciplinas = lazyWithRetry(() => import("@/pages/Disciplinas"), 'Disciplinas');
const BibliografiasManutencao = lazyWithRetry(() => import("@/pages/BibliografiasManutencao"), 'BibliografiasManutencao');
const RelatorioBibliografiaMec = lazyWithRetry(() => import("@/pages/RelatorioBibliografiaMec"), 'RelatorioBibliografiaMec');
const LuminaSpaces = lazyWithRetry(() => import("@/pages/LuminaSpaces"), 'LuminaSpaces');

const ParametrosAcademicos = lazyWithRetry(() => import("@/pages/ParametrosAcademicos"), 'ParametrosAcademicos');
const Turmas = lazyWithRetry(() => import("@/pages/Turmas"), 'Turmas');
const PeaTurmaDisciplina = lazyWithRetry(() => import("@/pages/PeaTurmaDisciplina"), 'PeaTurmaDisciplina');
const Coordenacao = lazyWithRetry(() => import("@/pages/Coordenacao"), 'Coordenacao');
const PortalProfessor = lazyWithRetry(() => import("@/pages/portais/PortalProfessor"), 'PortalProfessor');
const PortalProfessorPEA = lazyWithRetry(() => import("@/pages/portais/PortalProfessorPEA"), 'PortalProfessorPEA');
const PortalProfessorDiarios = lazyWithRetry(() => import("@/pages/portais/PortalProfessorDiarios"), 'PortalProfessorDiarios');
const PortalProfessorTurmas = lazyWithRetry(() => import("@/pages/portais/PortalProfessorTurmas"), 'PortalProfessorTurmas');
const PortalProfessorTurmaDetalhe = lazyWithRetry(() => import("@/pages/portais/PortalProfessorTurmaDetalhe"), 'PortalProfessorTurmaDetalhe');
const DiarioClasse = lazyWithRetry(() => import("@/pages/portais/DiarioClasse"), 'DiarioClasse');

const PortalAluno = lazyWithRetry(() => import("@/pages/portais/PortalAluno"), 'PortalAluno');
const PortalAlunoMicro = lazyWithRetry(() => import("@/pages/portais/PortalAlunoMicro"), 'PortalAlunoMicro');
const PortalAlunoMicroExecucao = lazyWithRetry(() => import("@/pages/portais/PortalAlunoMicroExecucao"), 'PortalAlunoMicroExecucao');
const PortalAlunoPea = lazyWithRetry(() => import("@/pages/portais/PortalAlunoPea"), 'PortalAlunoPea');
const PortalCoordenacao = lazyWithRetry(() => import("@/pages/portais/PortalCoordenacao"), 'PortalCoordenacao');
const PortalCoordenacaoPeaDashboard = lazyWithRetry(() => import("@/pages/portais/PortalCoordenacaoPeaDashboard"), 'PortalCoordenacaoPeaDashboard');
const PortalCoordenacaoPeaAnalytics = lazyWithRetry(() => import("@/pages/portais/PortalCoordenacaoPeaAnalytics"), 'PortalCoordenacaoPeaAnalytics');
const PortalCoordenacaoIndicadores = lazyWithRetry(() => import("@/pages/portais/PortalCoordenacaoIndicadores"), 'PortalCoordenacaoIndicadores');
const AlunosDaTurma = lazyWithRetry(() => import("@/pages/turmas/AlunosDaTurma"), 'AlunosDaTurma');
const PlanejamentoDaTurma = lazyWithRetry(() => import("@/pages/turmas/PlanejamentoDaTurma"), 'PlanejamentoDaTurma');

const DiariosValidacao = lazyWithRetry(() => import("@/pages/portais/coordenacao/DiariosValidacao"), 'DiariosValidacao');
const SolicitacoesTurmas = lazyWithRetry(() => import("@/pages/portais/coordenacao/SolicitacoesTurmas"), 'SolicitacoesTurmas');
const FrequenciasTurmas = lazyWithRetry(() => import("@/pages/portais/coordenacao/FrequenciasTurmas"), 'FrequenciasTurmas');
const PendenciasPeriodo = lazyWithRetry(() => import("@/pages/portais/coordenacao/PendenciasPeriodo"), 'PendenciasPeriodo');
const PendenciasDaTurma = lazyWithRetry(() => import("@/pages/turmas/PendenciasDaTurma"), 'PendenciasDaTurma');
const PlanejamentoGeral = lazyWithRetry(() => import("@/pages/portais/coordenacao/PlanejamentoGeral"), 'PlanejamentoGeral');


const GestaoAprovacaoPea = lazyWithRetry(() => import("@/pages/portais/coordenacao/GestaoAprovacaoPea"), 'GestaoAprovacaoPea');
const Parametros = lazyWithRetry(() => import("@/pages/Parametros"), 'Parametros');
const RegularizacaoDocente = lazyWithRetry(() => import("@/pages/RegularizacaoDocente"), 'RegularizacaoDocente');
const OnboardingUniversal = lazyWithRetry(() => import("@/pages/OnboardingUniversal"), 'OnboardingUniversal');
const OnboardingAdmin = lazyWithRetry(() => import("@/pages/OnboardingAdmin"), 'OnboardingAdmin');
const Eventos = lazyWithRetry(() => import("@/pages/Eventos"), 'Eventos');
const EventosProgramacao = lazyWithRetry(() => import("@/pages/EventosProgramacao"), 'EventosProgramacao');
const EventosCheckin = lazyWithRetry(() => import("@/pages/EventosCheckin"), 'EventosCheckin');
const EventosPresenca = lazyWithRetry(() => import("@/pages/EventosPresenca"), 'EventosPresenca');
const EventoPublico = lazyWithRetry(() => import("@/pages/EventoPublico"), 'EventoPublico');
const ImportarEdital = lazyWithRetry(() => import("@/pages/ImportarEdital"), 'ImportarEdital');
const ValidarCertificado = lazyWithRetry(() => import("@/pages/ValidarCertificado"), 'ValidarCertificado');
const AcompanharInscricao = lazyWithRetry(() => import("@/pages/AcompanharInscricao"), 'AcompanharInscricao');
const AceitarConviteComissao = lazyWithRetry(() => import("@/pages/AceitarConviteComissao"), 'AceitarConviteComissao');
const TerminalAvaliador = lazyWithRetry(() => import("@/pages/TerminalAvaliador"), 'TerminalAvaliador');
const ValidacaoOnboarding = lazyWithRetry(() => import("@/pages/ValidacaoOnboarding"), 'ValidacaoOnboarding');
const AtividadesReprografia = lazyWithRetry(() => import("@/pages/Atividades"), 'AtividadesReprografia');
const SincronizacaoLegado = lazyWithRetry(() => import("@/pages/SincronizacaoLegado"), 'SincronizacaoLegado');
const TesteStorage = lazyWithRetry(() => import("@/pages/dev/TesteStorage"), 'TesteStorage');

const Relatorios = lazyWithRetry(() => import("@/pages/Relatorios"), 'Relatorios');
const EquipeTecnica = lazyWithRetry(() => import("@/pages/EquipeTecnica"), 'EquipeTecnica');
const Recrutamento = lazyWithRetry(() => import("@/pages/Recrutamento"), 'Recrutamento');
const Captacao = lazyWithRetry(() => import("@/pages/Captacao"), 'Captacao');
const CheckinParticipante = lazyWithRetry(() => import("@/pages/CheckinParticipante"), 'CheckinParticipante');
const PalestranteAutoServico = lazyWithRetry(() => import("@/pages/PalestranteAutoServico"), 'PalestranteAutoServico');
const LandingPagePublic = lazyWithRetry(() => import("@/pages/captacao/LandingPagePublic"), 'LandingPagePublic');
const InscricaoFunil = lazyWithRetry(() => import("@/pages/captacao/InscricaoFunil"), 'InscricaoFunil');
const KanbanCaptacao = lazyWithRetry(() => import("@/pages/captacao/KanbanPage"), 'KanbanPage');
const AsaasWebhooksAuditoria = lazyWithRetry(() => import("@/pages/captacao/AsaasWebhooksAuditoria"), 'AsaasWebhooksAuditoria');
const PricingPage = lazyWithRetry(() => import("@/pages/captacao/financeiro/PricingPage"), 'PricingPage');
const SimuladorPublico = lazyWithRetry(() => import("@/pages/captacao/financeiro/SimuladorPublico"), 'SimuladorPublico');
const CheckoutPublico = lazyWithRetry(() => import("@/pages/captacao/financeiro/CheckoutPublico"), 'CheckoutPublico');
const ContratoAssinatura = lazyWithRetry(() => import("@/pages/captacao/financeiro/ContratoAssinatura"), 'ContratoAssinatura');
const PortalCandidatoCaptacao = lazyWithRetry(() => import("@/pages/captacao/financeiro/PortalCandidatoCaptacao"), 'PortalCandidatoCaptacao');

// ── Portal do Candidato (público + autenticado) ────────────────────────────
const VagasLanding = lazyWithRetry(() => import("@/pages/candidato/VagasLanding"), 'VagasLanding');
const VagaDetalhes = lazyWithRetry(() => import("@/pages/candidato/VagaDetalhes"), 'VagaDetalhes');
const CandidatoCadastro = lazyWithRetry(() => import("@/pages/candidato/CandidatoCadastro"), 'CandidatoCadastro');
const CandidatoLogin = lazyWithRetry(() => import("@/pages/candidato/CandidatoLogin"), 'CandidatoLogin');
const CandidatoPerfil = lazyWithRetry(() => import("@/pages/candidato/CandidatoPerfil"), 'CandidatoPerfil');
const MinhasCandidaturas = lazyWithRetry(() => import("@/pages/candidato/MinhasCandidaturas"), 'MinhasCandidaturas');
const CandidatoPrivacidade = lazyWithRetry(() => import("@/pages/candidato/CandidatoPrivacidade"), 'CandidatoPrivacidade');
const CandidatoRecuperarSenha = lazyWithRetry(() => import("@/pages/candidato/CandidatoRecuperarSenha"), 'CandidatoRecuperarSenha');
const WizardAdmissao = lazyWithRetry(() => import("@/pages/candidato/WizardAdmissao"), 'WizardAdmissao');
const CandidatoNotificacoes = lazyWithRetry(() => import("@/pages/candidato/CandidatoNotificacoes"), 'CandidatoNotificacoes');
const CandidatoLayout = lazyWithRetry(() => import("@/components/candidato/CandidatoLayout"), 'CandidatoLayout');

// ── Fallback de carregamento ───────────────────────────────────────────────
const PageFallback = () => (
  <div className="flex flex-col gap-4 p-6">
    <Skeleton className="h-8 w-48" />
    <Skeleton className="h-64 w-full" />
  </div>
);

const deveExibirPainelPerformanceDev = () => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return false;
  return window.localStorage.getItem('edulumina:dev-perf') === '1';
};

// ── QueryClient com cache estratégico (v2) ─────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 2 * 60 * 1000,       // 2 minutos padrão
      gcTime: 10 * 60 * 1000,          // 10 minutos de garbage collection
      refetchOnWindowFocus: false,
    },
  },
});

// ── Persistência local do cache (stale-while-revalidate) ───────────────────
// Whitelist de chaves: só persistimos dados leves e específicos do usuário
// (bootstrap unificado). Nada de PII além do que ele já vê na própria sessão.
const PERSIST_WHITELIST = new Set([
  'bootstrap-usuario',
]);

if (typeof window !== 'undefined') {
  const persister = createSyncStoragePersister({
    storage: window.localStorage,
    key: 'edulumina-rq-cache-v1',
    throttleTime: 1000,
  });

  persistQueryClient({
    queryClient,
    persister,
    maxAge: 24 * 60 * 60 * 1000, // 24h
    buster: import.meta.env.VITE_APP_VERSION ?? 'v1',
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => {
        const key = Array.isArray(query.queryKey) ? query.queryKey[0] : null;
        return typeof key === 'string' && PERSIST_WHITELIST.has(key);
      },
    },
  });
}


const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        {deveExibirPainelPerformanceDev() ? (
          <Suspense fallback={null}>
            <DevPerfPanel />
          </Suspense>
        ) : null}
        <BrowserRouter>
          <Toaster />
          <Sonner position="top-right" closeButton richColors />
          <AuthProvider>
            <ActiveUnitProvider>
              <ActivePeriodoLetivoProvider>
                <GlobalDashboardGuard />
                <ErrorBoundary nivel="page">
                  <Suspense fallback={<PageFallback />}>
                    <Routes>

                  <Route path="/login" element={<Login />} />
                  <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                  <Route path="/oauth/consent" element={<OAuthConsent />} />
                  <Route path="/confianca" element={<Confianca />} />
                  <Route path="/trust" element={<Navigate to="/confianca" replace />} />
                  <Route path="/security" element={<Navigate to="/confianca" replace />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/auth/confirm" element={<AuthConfirm />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/aceitar-convite" element={<AceitarConvite />} />
                  <Route path="/evento/:id" element={<EventoPublico />} />
                  <Route path="/inscricao/acompanhar" element={<AcompanharInscricao />} />
                  <Route path="/validar-certificado" element={<ValidarCertificado />} />
                  <Route path="/validar-certificado/:codigo" element={<ValidarCertificado />} />
                  <Route path="/aceitar-convite-comissao" element={<AceitarConviteComissao />} />
                  <Route path="/checkin" element={<CheckinParticipante />} />
                  <Route path="/palestrante/:token" element={<PalestranteAutoServico />} />
                  <Route path="/inscricao/:slug" element={<LandingPagePublic />} />
                  <Route path="/captacao/inscricao/:token" element={<InscricaoFunil />} />

                  {/* Portal do Candidato — rotas públicas */}
                  <Route path="/vagas" element={<VagasLanding />} />
                  <Route path="/vagas/:id" element={<VagaDetalhes />} />
                  <Route path="/vagas/simulador" element={<SimuladorPublico />} />
                  <Route path="/vagas/checkout" element={<CheckoutPublico />} />
                  <Route path="/vagas/contrato/:token" element={<ContratoAssinatura />} />
                  <Route path="/portal-captacao" element={<PortalCandidatoCaptacao />} />
                  <Route path="/candidato/cadastro" element={<CandidatoCadastro />} />
                  <Route path="/candidato/login" element={<CandidatoLogin />} />
                  <Route path="/candidato/recuperar-senha" element={<CandidatoRecuperarSenha />} />

                  {/* Portal do Candidato — rotas autenticadas (sem role institucional) */}
                  <Route path="/candidato" element={<CandidatoLayout />}>
                    <Route index element={<Navigate to="/candidato/perfil" replace />} />
                    <Route path="perfil" element={<CandidatoPerfil />} />
                    <Route path="candidaturas" element={<MinhasCandidaturas />} />
                    <Route path="privacidade" element={<CandidatoPrivacidade />} />
                    <Route path="admissao" element={<WizardAdmissao />} />
                    <Route path="notificacoes" element={<CandidatoNotificacoes />} />
                  </Route>

                  <Route path="/" element={<HomeRedirect />} />
                  <Route path="/onboarding" element={<ProtectedRoute />}>
                    <Route index element={<OnboardingUniversal />} />
                  </Route>
                  <Route path="/regularizacao-docente" element={<Navigate to="/onboarding" replace />} />

                  {/* Mobile Scan — fora do AppLayout */}
                  <Route element={<ProtectedRoute />}>
                    <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'provas']} />}>
                      <Route path="/provas/scan" element={<ProvasScan />} />
                      <Route path="/gestao/equipamentos/scan" element={<AtivosScan />} />
                    </Route>
                  </Route>

                  <Route element={<ProtectedRoute />}>
                    <Route element={<AppLayout />}>
                      <Route element={<DashboardGuard />}>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/dashboard/*" element={<Dashboard />} />
                      </Route>


                      <Route element={<ProtectedRoute requiredRoles={['admin', 'professor']} />}>
                        <Route path="/portal/professor" element={<PortalProfessor />} />
                        <Route path="/portal/professor/pea" element={<PortalProfessorPEA />} />
                        <Route path="/portal/professor/diarios" element={<PortalProfessorDiarios />} />
                        <Route path="/portal/professor/turmas" element={<PortalProfessorTurmas />} />
                        <Route path="/portal/professor/turmas/:turmaId" element={<PortalProfessorTurmaDetalhe />} />
                        <Route path="/portal/professor/turmas/:turmaId/diario" element={<DiarioClasse />} />
                        <Route path="/portal/professor/diario/:turmaId" element={<DiarioClasse />} />
                      </Route>


                      <Route element={<ProtectedRoute requiredRoles={['admin', 'aluno']} />}>
                        <Route path="/portal/aluno" element={<PortalAluno />} />
                        <Route path="/portal/aluno/micro" element={<PortalAlunoMicro />} />
                        <Route path="/portal/aluno/micro/:id" element={<PortalAlunoMicroExecucao />} />
                        <Route path="/portal/aluno/pea/:tdId" element={<PortalAlunoPea />} />
                      </Route>
                      <Route element={<ProtectedRoute requiredRoles={['admin', 'coordenacao']} />}>
                        <Route path="/portal/coordenacao" element={<PortalCoordenacao />} />
                        <Route path="/portal/coordenacao/peas" element={<GestaoAprovacaoPea />} />
                        <Route path="/portal/coordenacao/pea-dashboard" element={<PortalCoordenacaoPeaDashboard />} />
                       <Route path="/portal/coordenacao/pea-analytics" element={<PortalCoordenacaoPeaAnalytics />} />
                       <Route path="/portal/coordenacao/indicadores" element={<PortalCoordenacaoIndicadores />} />
                      <Route path="/portal/coordenacao/diarios" element={<DiariosValidacao />} />
                      <Route path="/portal/coordenacao/pedidos" element={<SolicitacoesTurmas />} />
                      <Route path="/portal/coordenacao/frequencias" element={<FrequenciasTurmas />} />
                      <Route path="/portal/coordenacao/planejamento" element={<PlanejamentoGeral />} />
                      <Route path="/portal/coordenacao/pendencias" element={<PendenciasPeriodo />} />
                      <Route
                        path="/portal/coordenacao/solicitacoes"
                        element={<Navigate to="/portal/coordenacao/pedidos" replace />}
                      />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'coordenacao', 'professor']} />}>
                        <Route path="/turmas/:turmaDisciplinaId/alunos" element={<AlunosDaTurma />} />
                        <Route path="/turmas/:turmaDisciplinaId/planejamento" element={<PlanejamentoDaTurma />} />
                        <Route path="/turmas/:turmaDisciplinaId/pendencias" element={<PendenciasDaTurma />} />
                      </Route>




                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'rh', 'secretaria', 'coordenacao']} />}>
                        <Route path="/professores" element={<Professores />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'secretaria']} />}>
                        <Route path="/pessoas" element={<Pessoas />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'rh']} />}>
                        <Route path="/equipe-tecnica" element={<EquipeTecnica />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'secretaria', 'ti', 'captacao']} />}>
                        <Route path="/captacao" element={<Captacao />} />
                        <Route path="/captacao/kanban/:processoId" element={<KanbanCaptacao />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'captacao', 'financeiro']} />}>
                        <Route path="/captacao/asaas-webhooks" element={<AsaasWebhooksAuditoria />} />
                        <Route path="/captacao/pricing" element={<PricingPage />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'rh', 'coordenacao']} />}>
                        <Route path="/recrutamento" element={<Recrutamento />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'professor', 'coordenacao', 'secretaria', 'provas']} />}>
                        <Route path="/impressao" element={<ImpressaoProvas />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'laboratorio', 'provas']} />}>
                        <Route path="/impressao/checkin/:id" element={<CheckinImpressao />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'professor', 'coordenacao', 'secretaria', 'laboratorio', 'infraestrutura']} />}>
                        <Route path="/gestao/equipamentos" element={<EquipamentosGestao />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'professor', 'coordenacao', 'laboratorio']} />}>
                        <Route path="/laboratorios" element={<Laboratorios />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'rh', 'secretaria']} />}>
                        <Route path="/convites" element={<Navigate to="/onboarding-admin" replace />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'rh', 'coordenacao']} />}>
                        <Route path="/usuarios" element={<Members />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'rh', 'secretaria']} />}>
                        <Route path="/unidades" element={<Unidades />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti']} />}>
                        <Route path="/auditoria" element={<Audit />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'rh', 'financeiro']} />}>
                        <Route path="/auditoria/dados-bancarios" element={<AuditoriaDadosBancarios />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'secretaria', 'coordenacao']} />}>
                        <Route path="/disciplinas" element={<Disciplinas />} />
                        <Route path="/cursos" element={<Cursos />} />
                        <Route path="/calendario" element={<CalendarioAcademico />} />
                        <Route path="/turmas" element={<Turmas />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'secretaria', 'coordenacao', 'bibliotecario']} />}>
                        <Route path="/bibliografias" element={<BibliografiasManutencao />} />
                        <Route path="/bibliografias/relatorio-mec" element={<RelatorioBibliografiaMec />} />
                      </Route>

                      {/* PEA: professor responsável também precisa abrir/editar — RLS/RPC garantem o gate fino. */}
                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'secretaria', 'coordenacao', 'professor']} />}>
                        <Route path="/turmas/:turmaId/disciplinas/:tdId/pea" element={<PeaTurmaDisciplina />} />
                      </Route>


                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'secretaria']} />}>
                        <Route path="/parametros-academicos" element={<ParametrosAcademicos />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'rh', 'secretaria']} />}>
                        <Route path="/coordenacao" element={<Coordenacao />} />
                        <Route path="/onboarding-admin" element={<OnboardingAdmin />} />
                        <Route path="/validacao-onboarding" element={<Navigate to="/onboarding-admin" replace />} />
                      </Route>




                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'rh', 'secretaria', 'coordenacao']} />}>
                        <Route path="/relatorios" element={<Relatorios />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin']} />}>
                        <Route path="/parametros" element={<Parametros />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti']} />}>
                        <Route path="/sincronizacao-legado" element={<SincronizacaoLegado />} />
                        <Route path="/admin/emails" element={<AdminEmails />} />
                        <Route path="/admin/teste-storage" element={<TesteStorage />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'infraestrutura', 'secretaria', 'coordenacao', 'laboratorio', 'financeiro', 'professor']} />}>
                        <Route path="/espacos" element={<LuminaSpaces />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'professor', 'reprografia']} />}>
                        <Route path="/atividades" element={<AtividadesReprografia />} />
                      </Route>

                      <Route element={<ProtectedRoute requiredRoles={['admin', 'ti', 'coordenacao', 'gestor_evento']} />}>
                        <Route path="/eventos" element={<Eventos />} />
                        <Route path="/eventos/importar-edital" element={<ImportarEdital />} />
                        <Route path="/eventos/programacao" element={<EventosProgramacao />} />
                        <Route path="/eventos/checkin" element={<EventosCheckin />} />
                        <Route path="/eventos/presenca" element={<EventosPresenca />} />
                      </Route>

                      <Route path="/terminal-avaliador" element={<TerminalAvaliador />} />
                    </Route>
                  </Route>

                    <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </ActivePeriodoLetivoProvider>
            </ActiveUnitProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
